import { Award, Users, Clock, Shield, Target, Lightbulb, Heart, Handshake } from 'lucide-react'
import SEOHead from '../components/SEOHead'

export default function About() {
  const stats = [
    { number: "30,000+", label: "Candidates", icon: Users },
    { number: "Award", label: "Winning", icon: Award },
    { number: "24/7", label: "Support", icon: Clock },
    { number: "100%", label: "Compliant", icon: Shield }
  ]

  const values = [
    {
      icon: Target,
      title: "Consultative Approach",
      description: "We listen to, understand, and exceed our clients' requirements, helping clients and candidates sail through their various challenges."
    },
    {
      icon: Heart,
      title: "Results Driven",
      description: "Focusing solely on results, delivering quality service and authentic performance that really creates long-lasting business relationships."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Constantly evolving through latest technology, streamlined processes, and efficient service delivery enhancing user experience."
    },
    {
      icon: Handshake,
      title: "Friendly & Approachable",
      description: "Our team maintains a warm, professional atmosphere fostering strong relationships with outstanding levels of service."
    }
  ]

  const team = [
    {
      name: "Sam Wharfe",
      position: "Chief Executive Officer & Founder",
      department: "Leadership",
      bio: "Sam founded C9 Recruitment with a vision to revolutionize transport and logistics staffing. With over a decade of industry experience, he brings deep market knowledge and strategic leadership to drive the company's growth and success."
    },
    {
      name: "Alfie Rae",
      position: "Operations Director",
      department: "Operations",
      bio: "Alfie oversees daily operations and client relationships. His expertise in logistics coordination and team management ensures seamless service delivery and exceptional client satisfaction across all recruitment campaigns."
    },
    {
      name: "Daniel Lunn",
      position: "Commercial Director",
      department: "Business Development",
      bio: "Daniel boasts a strong commercial background and an excellent reputation within the UK PSV industry. Following a 25-year career in publishing, he joined C9 Recruitment in August 2024 to drive brand expansion."
    },
    {
      name: "Luke Robinson",
      position: "Non-Exec Director (Compliance)",
      department: "Compliance",
      bio: "Luke specializes in compliance and regulatory oversight. His analytical approach and industry connections help ensure C9 Recruitment maintains the highest standards in the transport sector."
    }
  ]

  return (
    <div className="min-h-screen">
      <SEOHead
        title="About C9 Recruitment - Leading Transport & Logistics Recruitment Specialists"
        description="Meet the C9 Recruitment team. Award-winning recruitment specialists with decades of experience in transport, logistics, and technical recruitment across the UK."
        keywords="about C9 recruitment, transport recruitment team, logistics recruitment experts, recruitment agency team, UK recruitment specialists"
      />
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-c9-blue/80 to-transparent"></div>
        
        {/* Floating shapes for visual interest */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-c9-orange/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
        
        <div className="relative container-full py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-c9-orange/20 rounded-full text-c9-orange font-semibold text-sm mb-6 backdrop-blur-sm">
              About C9 Recruitment
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              We build a vision to serve the{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                needs of our customers
              </span>{" "}
              to their best and 
              respect the manner leading to fast-moving trends.
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Award-winning specialists delivering exceptional recruitment solutions 
              across transport, logistics, and technical sectors.
            </p>
          </div>
        </div>
      </section>

      {/* Enhanced Vision Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <div className="inline-block px-4 py-2 bg-c9-orange/10 rounded-full text-c9-orange font-semibold text-sm mb-4">
                  Our Vision
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6 leading-tight">
                  Transforming the Future of{" "}
                  <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                    Recruitment
                  </span>
                </h2>
              </div>
              
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                <p>
                  We aspire to build lasting relationships with both our clients and our job seekers. 
                  We want a recruitment agency that our clients can trust to deliver every time they need our 
                  services, and our candidate network can rely on us every time they need our guidance.
                </p>
                <p>
                  We work hand in hand with people who collaborate and move seamlessly, cutting through the 
                  noise in our sector, faster than our competitors.
                </p>
                <p>
                  We follow a stringent approach to evaluate each and every candidate, to ensure that we 
                  render only quality staff to our clients, which leads to a win-win process for both 
                  sides alike.
                </p>
              </div>
            </div>

            {/* Enhanced Stats Grid */}
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon
                return (
                  <div 
                    key={index} 
                    className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                  >
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-c9-orange to-orange-400 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-3xl md:text-4xl font-black text-c9-blue group-hover:text-c9-orange transition-colors duration-300">
                        {stat.number}
                      </div>
                      <div className="text-gray-600 font-semibold text-lg">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Values Section */}
      <section className="section-padding bg-white">
        <div className="container-full">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-c9-blue/10 rounded-full text-c9-blue font-semibold text-sm mb-4">
              Our Core Values
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Guiding Principles That{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Define Us
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do and shape how we serve our clients, 
              candidates, and partners every day.
            </p>
          </div>

          <div className="space-y-12">
            {values.map((value, index) => {
              const IconComponent = value.icon
              const isEven = index % 2 === 0
              
              return (
                <div 
                  key={index}
                  className={`flex flex-col lg:flex-row items-center gap-12 ${isEven ? '' : 'lg:flex-row-reverse'}`}
                >
                  {/* Icon and Title */}
                  <div className="lg:w-1/2 text-center lg:text-left">
                    <div className={`inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-c9-orange to-orange-400 shadow-lg ${isEven ? 'lg:mr-auto' : 'lg:ml-auto'} mb-6`}>
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-c9-blue mb-4">
                      {value.title}
                    </h3>
                  </div>
                  
                  {/* Description */}
                  <div className="lg:w-1/2">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
                      <p className="text-gray-700 text-lg leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Enhanced Team Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-c9-orange/10 rounded-full text-c9-orange font-semibold text-sm mb-4">
              Meet Our Team
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-6">
              Experienced Leaders in{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Recruitment
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced team brings decades of combined experience in recruitment, 
              specializing in industry knowledge.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {team.map((member, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100"
              >
                {/* Member Info Header */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-c9-orange to-orange-400 rounded-2xl flex items-center justify-center text-white text-2xl font-bold group-hover:scale-110 transition-transform duration-300">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-c9-blue group-hover:text-c9-orange transition-colors duration-300">
                      {member.name}
                    </h3>
                    <p className="text-gray-600 font-semibold">{member.position}</p>
                    <div className="inline-block px-3 py-1 bg-c9-blue/10 rounded-full text-c9-blue text-sm font-medium mt-1">
                      {member.department}
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                  <p className="text-gray-700 leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Awards Section */}
      <section className="section-padding bg-gradient-to-br from-c9-blue via-c9-blue to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Floating shapes */}
        <div className="absolute top-10 right-10 w-40 h-40 bg-c9-orange/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        
        <div className="relative container-full">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-c9-orange/20 rounded-full text-c9-orange font-semibold text-sm mb-4 backdrop-blur-sm">
              Awards & Recognition
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Industry{" "}
              <span className="bg-gradient-to-r from-c9-orange to-orange-400 bg-clip-text text-transparent">
                Recognition
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="group bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-c9-orange to-orange-400 rounded-2xl flex items-center justify-center">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Transport & Logistics</h3>
                  <p className="text-gray-200">Agency of the Year 2023</p>
                </div>
              </div>
            </div>

            <div className="group bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-c9-orange to-orange-400 rounded-2xl flex items-center justify-center">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Excellence Award</h3>
                  <p className="text-gray-200">Customer Service 2023</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="section-padding bg-gradient-to-r from-c9-orange to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Floating shapes */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-2xl transform -translate-x-24 translate-y-24"></div>
        
        <div className="relative container-full text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Join our growing family of satisfied clients and successful candidates. 
            Let's build something amazing together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="bg-white text-c9-orange px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Work With Us
            </a>
            <a 
              href="/candidates" 
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-c9-orange transition-all duration-300"
            >
              Join Our Team
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
